/**
 * Shared display-layer helpers for filter-condition chips. Used by the
 * filter-chip surfaces — the list-page toolbar chips and the filter tooltip —
 * so they apply IDENTICAL rules for "is this condition renderable?" and
 * "resolve association value(s) to entity ids", instead of each surface
 * maintaining its own copy and risking drift as these rules evolve.
 */

import type { FilterCondition } from '@/lib/crm/filters/types';

/** Operators that stand on their own with no value. */
const VALUELESS_OPERATORS = ['is_empty', 'is_not_empty'];

/**
 * A condition is renderable as a chip when its operator needs no value, or it
 * has a non-empty value. Empty arrays (e.g. in/not_in with no selections) and
 * null/undefined/empty-string values are treated as not-yet-complete.
 */
export function isFilterConditionValid(condition: FilterCondition): boolean {
  const needsValue = !VALUELESS_OPERATORS.includes(condition.operator);
  if (!needsValue) {
    return true;
  }
  if (
    condition.value === null ||
    condition.value === undefined ||
    condition.value === ''
  ) {
    return false;
  }
  if (Array.isArray(condition.value) && condition.value.length === 0) {
    return false;
  }
  return true;
}

/**
 * Derive the entity ids referenced by an association filter condition's value.
 * The value is a single id or an array of ids. Drops falsy entries BEFORE
 * string coercion (so `0`/`false`/`null`/`undefined`/`''` — none of which are
 * valid entity ids — never render a spurious "Unavailable"/loading badge for
 * a stringified "0"/"false"), then coerces the survivors to strings and
 * de-duplicates to avoid React duplicate-key warnings + silently-dropped
 * badges.
 */
export function deriveAssociationValueIds(value: unknown): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  const raw = Array.isArray(value) ? value : [value];
  const ids = raw
    .filter(v => Boolean(v))
    .map(v => String(v))
    .filter(id => id.length > 0);
  return Array.from(new Set(ids));
}
